'use client';

import { useMemo } from 'react';
import { ReactElement, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { tv } from 'tailwind-variants';

import {
  JumpProps,
  ProgressiveModalProps,
  ProgressiveModalDefaultKeys,
  ProgressiveModalDirectionKeys,
  ProvidedProgressiveModalProps,
} from './progressive-modal.types';
import { ProgressiveModalContext, useProgressiveModal } from './progressive-modal.hook';
import { progressiveModalAnimationVariants, progressiveModalTransition } from './progressive-modal.consts';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/utils/cn-tw-merge';

const progressiveModalStyle = tv({
  slots: {
    contentWrapper: ['bg-white h-full overflow-x-hidden overflow-y-auto outline-none outline-offset-0'],
  },
  variants: {
    padding: {
      true: {
        contentWrapper: ['p-5'],
      },
      false: {
        contentWrapper: ['p-0'],
      },
    },
    layout: {
      scroll: {},
      column: {
        contentWrapper: ['flex flex-col'],
      },
    },
  },
  defaultVariants: {
    padding: true,
    layout: 'scroll',
  },
});

export function ProgressiveModal({
  children,
  initialKey,
  description,
  open,
  handleClose,
  onKeyChange,
  contentPadding = true,
  contentLayout = 'scroll',
  ...rest
}: ProgressiveModalProps) {
  const [currentKey, setCurrentKey] = useState<string>(initialKey);

  const [slideDirection, setSlideDirection] = useState<ProgressiveModalDirectionKeys>(
    ProgressiveModalDirectionKeys.right,
  );

  useEffect(() => {
    setCurrentKey(initialKey);
  }, [initialKey, open]);

  const handleJump = ({ key, returning = false }: JumpProps<string>): void => {
    setSlideDirection(returning ? ProgressiveModalDirectionKeys.right : ProgressiveModalDirectionKeys.left);

    if (key === ProgressiveModalDefaultKeys.CLOSE) {
      setSlideDirection(ProgressiveModalDirectionKeys.center);
      handleClose({ currentKey, handleJump });
      return;
    }

    setCurrentKey(key);
    onKeyChange?.(key);
  };

  const onClose = (): void => {
    handleClose({ currentKey, handleJump });
  };

  const currentStep = useMemo(
    () => (children as ReactElement[]).find((child) => child?.key === currentKey),
    [children, currentKey],
  );

  return (
    <ProgressiveModalContext.Provider
      value={{
        handleJump,
        currentKey,
        onClose,
      }}
    >
      <ProvidedProgressiveModal
        description={description}
        open={open}
        slideDirection={slideDirection}
        contentPadding={contentPadding}
        contentLayout={contentLayout}
        {...rest}
      >
        {currentStep}
      </ProvidedProgressiveModal>
    </ProgressiveModalContext.Provider>
  );
}

function ProvidedProgressiveModal({
  children,
  description,
  className,
  slideDirection,
  open,
  hasHeader,
  title,
  contentPadding = true,
  contentLayout = 'scroll',
  onCloseAutoFocus,
  onOpenAutoFocus,
  onInteractOutside,
  onEscapeKeyDown,
  sheetSide,
  sheetSideByKey,
  ...rest
}: ProvidedProgressiveModalProps) {
  const { contentWrapper } = progressiveModalStyle({ padding: contentPadding, layout: contentLayout });
  const { currentKey, onClose, handleJump } = useProgressiveModal<string>();

  const resolvedSheetSide = sheetSideByKey?.[currentKey] ?? sheetSide ?? 'center-v2';

  return (
    <Sheet
      open={open}
      onOpenChange={() => {
        onClose();
      }}
    >
      <SheetContent
        side={resolvedSheetSide}
        closingTagPosition="right"
        className={cn('overflow-hidden p-0', className)}
        hasHeader={hasHeader}
        title={title}
        description={description}
        onOpenAutoFocus={(event) => onOpenAutoFocus?.({ event, currentKey, handleJump })}
        onCloseAutoFocus={(event) => onCloseAutoFocus?.({ event, currentKey, handleJump })}
        onInteractOutside={(event) => onInteractOutside?.({ event, currentKey, handleJump })}
        onEscapeKeyDown={(event) => onEscapeKeyDown?.({ event, currentKey, handleJump })}
        {...(rest as any)}
      >
        <AnimatePresence mode="popLayout" initial={false} custom={slideDirection}>
          {children && (
            <motion.div
              key={currentKey}
              custom={slideDirection}
              initial="enter"
              animate="center"
              exit="exit"
              variants={progressiveModalAnimationVariants}
              transition={progressiveModalTransition}
              className={contentWrapper()}
              style={{ maxHeight: 'inherit', height: 'inherit' }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  );
}
